import { Button } from "@120wateraudit/waterworks";
import { ValidationErrors } from "final-form";
import React, { useCallback, useMemo } from "react";
import { Form as FinalForm } from "react-final-form";
import { Form } from "semantic-ui-react";

import styled from "styled-components";

import Error from "src/components/Error";
import { isApiError } from "src/utils/apiError";
import { toastError, toastSuccess } from "src/utils/toast";
import LoadingIndicatorPage from "src/components/LoadingIndicatorPage";
import {
  useDeleteTerritoryMutation,
  useGetSystemsCountByTerritoryIdQuery,
} from "src/services";

const validate = (): ValidationErrors => {
  const errors: ValidationErrors = {};

  return errors;
};

interface DeleteModalProps {
  onClose: () => void;
  territoryId: number
}

const DeleteTerritoryModal = ({
  onClose,
  territoryId,
}: DeleteModalProps): JSX.Element => {
  const [deleteTerritory, { error, isError, isLoading }] =
    useDeleteTerritoryMutation();

  const { data: systemCount, isFetching } = useGetSystemsCountByTerritoryIdQuery(territoryId);

  const onSubmit = useCallback(
    async () => {
      try {
        await deleteTerritory(
          { territoryId }
        );
        toastSuccess("Successfully deleted territory");
        onClose();
      } catch {
        toastError("Could not delete territory. Please try again.");
      }
    },
    [deleteTerritory]
  );

  const errors = useMemo(() => {
    if (!error || !isError) {
      return undefined;
    }

    if (isApiError(error)) {
      return [error.data?.error];
    }

    if (error !== null && typeof error === 'object' && 'data' in error) {
      const data = error.data
      if (data !== null && typeof data === 'object' && 'consumerErrorMessage' in data) {
        const errorMessage = data.consumerErrorMessage as string
        if (errorMessage.includes('BadRequestException')) {
          return [errorMessage.replace('BadRequestException: ', '')]
        }
      }
    }

    return ["Please try again or contact support@120water.com"];
  }, [error, isError]);

  if (isLoading || isFetching) {
    return <LoadingIndicatorPage />;
  }

  return (
    <ModalContentContainer>
      <FinalForm
        onSubmit={onSubmit}
        render={({ valid, handleSubmit, submitting }) => {
          return (
            <div style={{ width: "95%" }}>
              <FormContentContainer
                onSubmit={() => {
                  void handleSubmit();
                }}
              >
                <h2>Delete this territory?</h2>
                <p>Deleting this Territory will remove it from the list of assignable Territories, as well as remove its tag from {systemCount} Utilities.</p>
                <ButtonsWrapper>
                  <Button
                    type="submit"
                    disabled={submitting || isLoading || !valid}
                    variant="error"
                  >
                    Delete
                  </Button>
                  <Button onClick={onClose}>Cancel</Button>
                </ButtonsWrapper>
              </FormContentContainer>

              {errors && (
                <Error style={{ margin: "12px 24px" }} messages={errors} />
              )}
            </div>
          );
        }}
        validate={validate}
      />
    </ModalContentContainer>
  );
};

const ModalContentContainer = styled.div`
  display: flex;
  margin: 0 2rem;
`;

const FormContentContainer = styled(Form)`
  margin: 1.5rem 1rem;

  label {
    font-weight: bold;
    padding-bottom: 10px;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  margin: 1.5rem 1rem;
  display: flex;
  width: 75%;
  justify-content: space-between;
  button {
    width: 70%;
    margin-right: 15px;
  }
`;

export default DeleteTerritoryModal;
